import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type BillingPeriod,
  PRICING,
  TIER_ORDER,
  TIERS,
  type TierId,
  annualTotalCents,
  clampStreams,
  formatPriceCents,
  monthlyTotalCents,
  priceForPeriodCents,
} from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { buttonVariants } from '@/components/ui/Button';
import { CheckMark, CrossMark, Minus, Plus } from '@/components/icons';

function PeriodToggle({
  period,
  onChange,
}: {
  period: BillingPeriod;
  onChange: (p: BillingPeriod) => void;
}) {
  const pct = Math.round(PRICING.annualDiscount * 100);
  return (
    <div
      role="tablist"
      aria-label="Billing period"
      className="inline-flex items-center rounded-full border border-border bg-surface p-1"
    >
      {(['monthly', 'annual'] as const).map((p) => (
        <button
          key={p}
          role="tab"
          aria-selected={period === p}
          onClick={() => onChange(p)}
          className={cn(
            'rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors',
            period === p ? 'bg-brand-gradient text-white' : 'text-ink-muted hover:text-ink',
          )}
        >
          {p}
          {p === 'annual' && (
            <span className={cn('ml-1.5 text-2xs', period === p ? 'text-white/80' : 'text-success')}>
              save {pct}%
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function StreamSelector({
  streams,
  onChange,
}: {
  streams: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="inline-flex items-center gap-3">
      <span className="text-sm text-ink-muted">Simultaneous streams</span>
      <div className="inline-flex items-center rounded-full border border-border bg-surface">
        <button
          type="button"
          aria-label="Fewer streams"
          disabled={streams <= PRICING.includedStreams}
          onClick={() => onChange(clampStreams(streams - 1))}
          className="grid h-9 w-9 place-items-center rounded-full text-ink-muted transition-colors hover:text-ink disabled:opacity-40"
        >
          <Minus size={16} />
        </button>
        <span className="w-8 text-center font-display text-base font-bold tabular-nums text-ink">
          {streams}
        </span>
        <button
          type="button"
          aria-label="More streams"
          disabled={streams >= PRICING.maxStreams}
          onClick={() => onChange(clampStreams(streams + 1))}
          className="grid h-9 w-9 place-items-center rounded-full text-ink-muted transition-colors hover:text-ink disabled:opacity-40"
        >
          <Plus size={16} />
        </button>
      </div>
      <span className="text-2xs text-ink-faint">
        +{formatPriceCents(PRICING.extraStreamMonthlyCents)}/mo each · up to {PRICING.maxStreams}
      </span>
    </div>
  );
}

function PriceBlock({
  tier,
  streams,
  period,
}: {
  tier: TierId;
  streams: number;
  period: BillingPeriod;
}) {
  const total = priceForPeriodCents(tier, streams, period);
  if (period === 'annual') {
    const perMonth = Math.round(annualTotalCents(tier, streams) / 12);
    return (
      <div>
        <div className="flex items-baseline gap-1.5">
          <span className="font-display text-4xl font-extrabold text-ink">
            {formatPriceCents(total)}
          </span>
          <span className="text-sm text-ink-muted">/year</span>
        </div>
        <p className="mt-1 text-xs text-ink-faint">
          ≈ {formatPriceCents(perMonth)}/mo · paid once · does not auto-renew
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display text-4xl font-extrabold text-ink">
          {formatPriceCents(total)}
        </span>
        <span className="text-sm text-ink-muted">/month</span>
      </div>
      <p className="mt-1 text-xs text-ink-faint">
        {formatPriceCents(monthlyTotalCents(tier, streams))} billed monthly · cancel any time
      </p>
    </div>
  );
}

function FeatureRow({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2.5 text-sm">
      {ok ? <CheckMark size={18} /> : <CrossMark size={18} />}
      <span className={ok ? 'text-ink' : 'text-ink-faint'}>{children}</span>
    </li>
  );
}

export function PlansPage() {
  const [period, setPeriod] = useState<BillingPeriod>('monthly');
  const [streams, setStreams] = useState<number>(PRICING.includedStreams);

  return (
    <div className="container-page py-12 md:py-16">
      <div className="mx-auto max-w-2xl text-center">
        <p className="eyebrow mb-3">Plans &amp; pricing</p>
        <h1 className="font-display text-display-sm font-extrabold text-ink">
          Pick a plan. Watch everything.
        </h1>
        <p className="mt-4 text-ink-muted">
          Non-subscribers can always watch previews. Full episodes need an active plan. Change tiers,
          add streams, or cancel any time.
        </p>
      </div>

      <div className="mt-9 flex flex-col items-center justify-center gap-5 sm:flex-row sm:gap-8">
        <PeriodToggle period={period} onChange={setPeriod} />
        <StreamSelector streams={streams} onChange={setStreams} />
      </div>

      <div className="mt-10 grid gap-5 lg:grid-cols-3">
        {TIER_ORDER.map((id) => {
          const tier = TIERS[id];
          return (
            <div key={id} className="card relative flex flex-col p-7">
              <h2 className="font-display text-xl font-bold text-ink">{tier.name}</h2>
              <p className="mt-1 text-sm text-ink-muted">{tier.tagline}</p>
              <div className="my-6">
                <PriceBlock tier={id} streams={streams} period={period} />
              </div>
              <Link
                to={`/checkout?tier=${id}&period=${period}&streams=${streams}`}
                className={buttonVariants({ variant: 'primary', size: 'lg', className: 'w-full' })}
              >
                Choose {tier.name}
              </Link>
              <ul className="mt-7 space-y-3">
                <FeatureRow ok>Full episodes, JA &amp; EN audio</FeatureRow>
                <FeatureRow ok={tier.features.adFree}>
                  {tier.features.adFree ? 'Ad-free playback' : 'Ad-supported playback'}
                </FeatureRow>
                <FeatureRow ok>
                  {Math.round(tier.features.merchDiscountPct * 100)}% merch discount
                </FeatureRow>
                <FeatureRow ok={tier.features.offlineDownloads}>Offline downloads</FeatureRow>
                <FeatureRow ok={tier.features.gamesAccess}>Games access</FeatureRow>
                <FeatureRow ok={tier.features.highestQuality}>4K / highest quality</FeatureRow>
                <FeatureRow ok={tier.features.earlyAccess}>Early episode drops</FeatureRow>
                <FeatureRow ok>
                  {streams} simultaneous stream{streams > 1 ? 's' : ''}
                </FeatureRow>
              </ul>
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-center text-xs text-ink-faint">
        Owner, admins, and comped accounts watch everything free. Secure checkout is wired up in a
        later phase.
      </p>
    </div>
  );
}
